import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PiiDecryptService {
  private secretKey: string;

  constructor() {
    // Get secret key from environment (can be set via build-time variables)
    this.secretKey = environment.piiSecretKey;
    
    // Add enhanced global test methods for debugging (DEVELOPMENT ONLY)
    if (!environment.production) {
      (window as any).testDecryptService = this.globalTest.bind(this);
      (window as any).testAmountDecryption = this.testAmountDecryption.bind(this);
      (window as any).debugPiiDecryption = this.debugPiiDecryption.bind(this);
      console.log('🔧 PII Decrypt Service debugging methods available in development mode');
    }
  }

  /**
   * Decrypt PII data that was encrypted on the backend
   * Enhanced with better error handling and debugging for BigDecimal amounts
   * @param encryptedData - The encrypted/encoded string from backend
   * @returns Decrypted string
   */
  decrypt(encryptedData: string): string {
    if (!encryptedData || !this.secretKey) {
      return '[DECRYPTION ERROR]';
    }

    try {
      // Step 1: Base64 decode the encrypted data
      const encryptedBytes = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
      
      // Step 2: Convert secret key to UTF-8 bytes
      const keyBytes = new TextEncoder().encode(this.secretKey);
      
      // Step 3: XOR with secret key bytes
      const resultBytes = new Uint8Array(encryptedBytes.length);
      for (let i = 0; i < encryptedBytes.length; i++) {
        resultBytes[i] = encryptedBytes[i] ^ keyBytes[i % keyBytes.length];
      }
      
      // Step 4: Convert back to UTF-8 string
      const decryptedResult = new TextDecoder('utf-8').decode(resultBytes);
      
      return decryptedResult;
    } catch (error) {
      // Only log errors in development mode
      if (!environment.production) {
        console.error('❌ PII Decryption error:', {
          error: error instanceof Error ? error.message : String(error),
          dataLength: encryptedData.length,
          secretKeyPresent: !!this.secretKey
        });
      }
      return '[DECRYPTION ERROR]';
    }
  }

  /**
   * Check if a string is valid Base64
   */
  private isBase64(str: string): boolean {
    if (!str) return false;
    try {
      return btoa(atob(str)) === str;
    } catch (err) {
      return false;
    }
  }

  /**
   * Check if a string represents a numeric value (including BigDecimal)
   */
  private isNumeric(str: string): boolean {
    if (!str) return false;
    return !isNaN(Number(str)) && !isNaN(parseFloat(str));
  }

  /**
   * Check if a string is in BigDecimal format
   */
  private isBigDecimalFormat(str: string): boolean {
    if (!str) return false;
    // Check if it's a valid decimal number (can include decimal point)
    const decimalPattern = /^-?\d+(\.\d+)?$/;
    return decimalPattern.test(str);
  }

  /**
   * Check if a field contains encrypted data
   * Enhanced to better detect encrypted BigDecimal amounts and other PII
   */
  isEncrypted(data: string): boolean {
    if (!data) return false;
    
    // Check if it's a plain number (not encrypted)
    if (this.isNumeric(data)) {
      return false;
    }
    
    // Enhanced Base64 detection for encrypted data
    const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
    const isBase64Like = base64Pattern.test(data);
    
    // Additional checks for encrypted data:
    // 1. Must be at least 4 characters (minimum Base64)
    // 2. Must follow Base64 pattern
    // 3. Must be decodable as Base64
    if (isBase64Like && data.length >= 4) {
      try {
        // Try to decode as Base64 to verify it's valid
        const decoded = atob(data);
        // If decoded successfully and has content, it's likely encrypted
        return decoded.length > 0;
      } catch (e) {
        // If Base64 decode fails, it's not encrypted
        return false;
      }
    }
    
    return false;
  }

  /**
   * Get masked version of data for display
   * Enhanced for amount fields
   */
  getMaskedData(data: string, visibleStart: number = 3, visibleEnd: number = 1, maskChar: string = 'X'): string {
    if (!data) {
      return '';
    }
    
    // Special handling for amount fields
    if (this.isNumeric(data)) {
      // For amounts, show first and last digits with dots in between
      const parts = data.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];
      
      if (integerPart.length <= visibleStart + visibleEnd) {
        return maskChar.repeat(integerPart.length) + (decimalPart ? '.' + decimalPart : '');
      }
      
      const maskedInteger = integerPart.slice(0, visibleStart) + 
                           maskChar.repeat(integerPart.length - visibleStart - visibleEnd) + 
                           integerPart.slice(-visibleEnd);
      
      return maskedInteger + (decimalPart ? '.' + decimalPart : '');
    }
    
    // Default masking for non-numeric data
    if (data.length <= visibleStart + visibleEnd) {
      return maskChar.repeat(data.length);
    }
    
    const start = data.slice(0, visibleStart);
    const end = data.slice(-visibleEnd);
    const maskedMiddle = maskChar.repeat(data.length - visibleStart - visibleEnd);
    
    return `${start}${maskedMiddle}${end}`;
  }

  /**
   * Test amount decryption specifically
   */
  testAmountDecryption(encryptedAmount: string): void {
    console.log('�� === Testing Amount Decryption ===');
    console.log('🔵 Encrypted Amount:', encryptedAmount);
    
    // Test decryption
    const decrypted = this.decrypt(encryptedAmount);
    console.log('🟢 Decrypted Amount:', decrypted);
    
    // Check if it's a valid amount
    const isValidAmount = this.isNumeric(decrypted) && decrypted !== '[DECRYPTION ERROR]';
    console.log('✅ Is Valid Amount:', isValidAmount);
    
    if (isValidAmount) {
      const maskedAmount = this.getMaskedData(decrypted, 2, 2);
      console.log('🎭 Masked Amount:', maskedAmount);
    }
    
    console.log('🧪 === End Amount Test ===');
  }

  /**
   * Enhanced debugging for PII decryption
   */
  debugPiiDecryption(testData: string): void {
    console.log('🔍 === PII Decryption Debug ===');
    console.log('📋 Test Data:', testData);
    console.log('🔐 Secret Key Present:', !!this.secretKey);
    console.log('📏 Secret Key Length:', this.secretKey?.length || 0);
    
    // Check if data looks encrypted
    console.log('🔍 Is Encrypted:', this.isEncrypted(testData));
    console.log('🔍 Is Base64:', this.isBase64(testData));
    console.log('🔍 Is Numeric:', this.isNumeric(testData));
    
    // Try decryption
    if (this.isEncrypted(testData)) {
      const decrypted = this.decrypt(testData);
      console.log('🟢 Decrypted:', decrypted);
      console.log('🔍 Decrypted Is Numeric:', this.isNumeric(decrypted));
      console.log('🔍 Decrypted Is BigDecimal Format:', this.isBigDecimalFormat(decrypted));
    } else {
      console.log('⚠️ Data doesn\'t appear to be encrypted');
    }
    
    console.log('🔍 === End Debug ===');
  }

  /**
   * Global test method that can be called from browser console
   */
  globalTest(): void {
    console.log('🧪 === Decrypt Service Global Test ===');
    
    // Test with a simple value
    const testValue = 'Successful';
    console.log('🔵 Original:', testValue);
    
    // Encrypt it manually
    const encrypted = this.encryptForTest(testValue);
    console.log('🔴 Encrypted:', encrypted);
    
    // Decrypt it
    const decrypted = this.decrypt(encrypted);
    console.log('🟢 Decrypted:', decrypted);
    console.log('✅ Test passed:', testValue === decrypted);
    
    // Test with BigDecimal amounts
    const testAmount = '1234.56';
    console.log('🔵 Test Amount:', testAmount);
    const encryptedAmount = this.encryptForTest(testAmount);
    console.log('🔴 Encrypted Amount:', encryptedAmount);
    const decryptedAmount = this.decrypt(encryptedAmount);
    console.log('🟢 Decrypted Amount:', decryptedAmount);
    console.log('✅ Amount test passed:', testAmount === decryptedAmount);
    
    console.log('🧪 === End Service Test ===');
  }

  /**
   * Encrypt a value for testing
   */
  private encryptForTest(value: string): string {
    if (!value) return '';
    
    const encoder = new TextEncoder();
    const testKey = this.secretKey || environment.piiSecretKey || 'test-key';
    
    try {
      const textBytes = encoder.encode(value);
      const keyBytes = encoder.encode(testKey);
      
      // XOR encryption
      const encrypted = new Uint8Array(textBytes.length);
      for (let i = 0; i < textBytes.length; i++) {
        encrypted[i] = textBytes[i] ^ keyBytes[i % keyBytes.length];
      }
      
      // Convert to base64
      return btoa(String.fromCharCode(...encrypted));
    } catch (error) {
      console.error('Test encryption failed:', error);
      return value;
    }
  }
} 